import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ArrowRight,
  Clock,
  Search,
  Briefcase,
  Scale,
  Settings,
  HeartHandshake,
} from "lucide-react";
import { Container, CardMotion } from "@/components/site/Primitives";
import { CategoryPill } from "@/components/site/CategoryPill";
import { loadStories } from "@/lib/content-loader";
import { formatPublicDateShort } from "@/lib/format-public-date";
import penangGeorgetown from "@/assets/ksl-lounge-meeting.png.asset.json";

type StoryItem = Awaited<ReturnType<typeof loadStories>>[number];

export const Route = createFileRoute("/client-stories/")({
  validateSearch: (search: Record<string, unknown>) => ({
    topic: typeof search.topic === "string" ? search.topic : "",
  }),
  loader: () => loadStories(),
  head: () => ({
    meta: [
      { title: "Client Stories — KS Lau & Co" },
      { name: "description", content: "Real client outcomes from KS Lau & Co — tax, advisory and operational engagements that helped businesses move forward." },
      { property: "og:title", content: "Client Stories — KS Lau & Co" },
      { property: "og:description", content: "Selected client outcomes across tax, audit and advisory engagements." },
    ],
  }),
  component: ClientStoriesIndex,
});

const CATEGORY_HIGHLIGHTS = [
  {
    Icon: Scale,
    title: "Tax & Disputes",
    body: "Resolving complex tax matters through constructive engagement and well-supported representations.",
  },
  {
    Icon: Briefcase,
    title: "Business Advisory",
    body: "Helping businesses plan, structure and scale with greater financial clarity and discipline.",
  },
  {
    Icon: Settings,
    title: "Operations & Controls",
    body: "Documenting processes, strengthening governance and building scalable operating frameworks.",
  },
  {
    Icon: HeartHandshake,
    title: "Community Impact",
    body: "Supporting initiatives that create lasting positive impact in our communities.",
  },
];

const FILTERS = [
  "All Stories",
  "Tax",
  "Business Advisory",
  "Operations",
  "Community",
] as const;

function matchesFilter(story: StoryItem, f: (typeof FILTERS)[number]) {
  if (f === "All Stories") return true;
  const haystack = `${story.industry} ${story.service}`.toLowerCase();
  if (f === "Tax") return haystack.includes("tax");
  if (f === "Business Advisory") return haystack.includes("business advisory") || haystack.includes("advisory");
  if (f === "Operations") return haystack.includes("operations") || haystack.includes("controls");
  if (f === "Community") return haystack.includes("community") || haystack.includes("pro bono");
  return true;
}

function formatDate(d: string) {
  return formatPublicDateShort(d);
}

function ClientStoriesIndex() {
  const clientStories = Route.useLoaderData();
  const { topic } = Route.useSearch();
  const initialFilter = FILTERS.includes(topic as any) ? (topic as (typeof FILTERS)[number]) : "All Stories";
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>(initialFilter);
  const [query, setQuery] = useState("");

  const featured = clientStories[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = clientStories.filter((s) => {
      const mf = matchesFilter(s, filter);
      const mq =
        !q ||
        s.title.toLowerCase().includes(q) ||
        s.excerpt.toLowerCase().includes(q) ||
        s.industry.toLowerCase().includes(q) ||
        s.service.toLowerCase().includes(q) ||
        (s.client?.toLowerCase().includes(q) ?? false);
      return mf && mq;
    });
    if (!q && filter === "All Stories" && featured) {
      return matches.filter((s) => s.slug !== featured.slug);
    }
    return matches;
  }, [filter, query, clientStories, featured]);

  return (
    <>
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <img
          src={penangGeorgetown.url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-r from-primary/90 via-primary/55 to-primary/10"
        />
        <Container>
          <div className="flex min-h-[32vh] flex-col justify-end py-6 text-primary-foreground md:min-h-[40vh] md:py-8">
            <h1 className="max-w-2xl text-4xl font-semibold leading-[1.1] tracking-tight md:text-5xl lg:text-6xl">
              Real outcomes.
              <br />
              Real businesses.
            </h1>
          </div>
        </Container>
      </section>

      {/* CATEGORY STRIP */}
      <section className="border-y border-border bg-stone/40">
        <Container>
          <div className="grid gap-8 py-10 md:grid-cols-4 md:gap-0">
            {CATEGORY_HIGHLIGHTS.map(({ Icon, title, body }, i) => (
              <div
                key={title}
                className={
                  "flex gap-4 md:px-6 " +
                  (i > 0 ? "md:border-l md:border-border" : "")
                }
              >
                <Icon className="h-8 w-8 shrink-0 text-gold" strokeWidth={1.5} />
                <div>
                  <h3 className="text-base font-semibold text-primary">{title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* STICKY FILTER + SEARCH */}
      <div className="sticky top-16 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <Container>
          <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {FILTERS.map((f) => {
                const active = filter === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFilter(f)}
                    className={
                      "transition " +
                      (active
                        ? "rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground"
                        : "px-1 py-1.5 text-sm font-medium text-muted-foreground hover:text-primary")
                    }
                  >
                    {f}
                  </button>
                );
              })}
            </div>
            <label className="relative flex items-center md:w-56">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search stories…"
                className="w-full rounded-full border border-border bg-white py-1.5 pl-4 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
              <Search className="pointer-events-none absolute right-3 h-4 w-4 text-muted-foreground" />
            </label>
          </div>
        </Container>
      </div>

      {/* FEATURED STORY */}
      {featured && filter === "All Stories" && !query && (
        <section className="bg-white py-10 md:py-12">
          <Container>
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <span className="text-sm font-semibold uppercase tracking-[0.12em] text-gold">
                  Featured Story
                </span>
                <h2 className="mt-3 text-2xl leading-tight text-primary md:text-3xl">
                  {featured.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {featured.excerpt}
                </p>
                <Link
                  to="/client-stories/$slug"
                  params={{ slug: featured.slug }}
                  aria-label={`Read story: ${featured.title}`}
                  className="mt-6 inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold bg-white text-gold transition hover:bg-gold hover:text-primary-foreground"
                >
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </Link>
                <div className="mt-5 flex items-center gap-3 text-sm text-muted-foreground">
                  <span>{formatDate(featured.date)}</span>
                  <span className="text-border">|</span>
                  <span>{featured.industry}</span>
                  <span className="text-border">|</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" /> {featured.readTime}
                  </span>
                </div>
              </div>
              <Link
                to="/client-stories/$slug"
                params={{ slug: featured.slug }}
                className="group block overflow-hidden rounded-lg"
              >
                <img
                  src={featured.cover}
                  alt={featured.title}
                  loading="lazy"
                  width={1280}
                  height={640}
                  className="aspect-[16/10] w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                />
              </Link>
            </div>
          </Container>
        </section>
      )}

      {/* STORY GRID */}
      <section className="py-14 md:py-16">
        <Container>
          {filtered.length === 0 ? (
            <p className="py-12 text-center text-base text-muted-foreground">
              No client stories match your search.
            </p>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {filtered.map((s, i) => (
                <Link
                  key={s.slug}
                  to="/client-stories/$slug"
                  params={{ slug: s.slug }}
                  className="group block h-full"
                >
                  <CardMotion
                    animateIndex={i}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-sm transition hover:shadow-md"
                  >
                  <div className="relative overflow-hidden">
                    <img
                      src={s.cover}
                      alt={s.title}
                      loading="lazy"
                      width={800}
                      height={500}
                      className="aspect-[4/3] w-full object-cover transition duration-700 group-hover:scale-[1.04]"
                    />
                    <div className="absolute bottom-3 left-3">
                      <CategoryPill label={s.industry} variant="overlay" />
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="text-lg leading-snug text-primary">{s.title}</h3>
                    <p className="mt-3 flex-1 text-base leading-relaxed text-muted-foreground">
                      {s.excerpt}
                    </p>
                    <div className="mt-5 flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">
                      <span>{formatDate(s.date)}</span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3" /> {s.readTime}
                      </span>
                    </div>
                    <span className="mt-4 inline-flex items-center text-gold transition group-hover:translate-x-1">
                      <span className="sr-only">Read story</span>
                      <ArrowRight aria-hidden="true" className="h-4 w-4" />
                    </span>
                  </div>
                  </CardMotion>
                </Link>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* CLOSING QUOTE */}
      <section className="border-t border-border py-16 md:py-20">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-2xl italic leading-relaxed text-primary md:text-3xl">
              <span className="text-gold">“</span>
              Behind every engagement is a business we've helped move forward.
              <span className="text-gold">”</span>
            </p>
          </div>
        </Container>
      </section>
    </>
  );
}
